import type { CountryCodeTBB, LangTBB } from '@src/i18n/locales'
import { createPrefixedCookies } from '@src/utils/cookies.util'

export const COOKIES = {
  ...createPrefixedCookies({
    session: 'bodi-session',
    /** Anonymous user session identifier (used when Shopify feature flag is enabled) */
    anonymousId: 'bodi-anonymous-id',
    /** Device IANA timezone, written client-side */
    timezone: 'bodi-timezone',

    auth0Session: 'bodi-auth0-session',
    /** Ensures the same callback URL is used for token request and code exchange */
    auth0CallbackUrl: 'bodi-auth0-callback-url',
    /** Where to redirect user after Auth0 auth flow is complete. */
    auth0RedirectUrl: 'bodi-auth0-redirect-url',
    /** Skip SSO check on Auth0 callback requests. */
    auth0ComingFromCallback: 'bodi-auth0-coming-from-callback',
    /** Ensures the Auth0 SSO check is done once until a page is finally loaded. */
    auth0CheckingSSO: 'bodi-auth0-checking-sso',
    /** Tracks when SSO was last checked to allow same-site SSO checks periodically. */
    auth0SSOCheckedRecently: 'bodi-auth0-sso-checked-recently',

    order: 'bodi-order',
    passwordCreated: 'bodi-password-created',
    royaltyCode: 'bodi-royalty-code',
    webSourceCode: 'bodi-web-source-code',
    upsellFromSkus: 'bodi-upsell-from-skus',
    upsellToSkus: 'bodi-upsell-to-skus',
    recaptcha: 'bodi-recaptcha',
    recaptchaToken: 'bodi-recaptcha-token',
    userProperties: 'bodi-user-properties',
    inspectEnabled: 'bodi-inspect-enabled',
    socialladderAmbassadorId: 'bodi-socialladder-ambassador-id',
    /** Tracks if the user has interacted with the geo redirection modal. Shows the modal if not. */
    geoRedirectionModal: 'bodi-geo-redirection-modal',
    lastActivityTimestamp: 'bodi-last-activity-timestamp',
    /** Used to store whether or not the user is bypassing - for internal use only */
    bypass: 'bodi-bypass',
    /** When set, skips SSO checks for the request. Used to bypass SSO for SEO crawlers. */
    ssoBypass: 'bodi-sso-bypass',
    sierraChatSession: 'bodi-sierra-chat-session',
    sierraLastBannerShown: 'bodi-sierra-last-banner-shown',
    /** User opted out of the post-login intermediary page */
    postLoginSkip: 'bodi-post-login-skip',
  }),
  /** Used for Vercel Deployment Protection to lock down our lower environments */
  vercelJwt: '_vercel_jwt',
  vercelJwtTracking: '_vercel_jwt_tracking',
}

export const QUERY_PARAMS = {
  gtmDisabled: 'bodi-gtm-disabled',
  locale: 'locale',
  referralProgramId: 'referralProgramId',
  trainerName: 'trainerName',
  royaltyCode: 'rc',
  sku: 'sku',
  quantity: 'quantity',
  redirectUrl: 'redirectUrl',
  redirectUrlAlt: 'redirect_url',
  sessionTransferToken: 'session_transfer_token',
  /** Why logout was initiated when not user-driven (inactivity, refresh_failed). */
  logoutReason: 'logoutReason',
  authLocale: 'authLocale',
  socialladderAmbassadorId: 'ambassadorID',
  nextDoorClickId: 'ndclid' as const,
  /** Enables/disables the bypass by setting true or false */
  bypass: 'bypass',
  /** Non-prod only: overrides the My Progress additional surface variant. */
  myProgress: 'myProgress',
  royaltyCodeAlt: 'rc',
  webSourceCodeAlt: 'code',
}

export const APP_HASH = {
  chat: 'chat',
  mediaPanel: 'media-panel',
  share: 'share',
} as const

export const APP_HASHES: string[] = Object.values(APP_HASH)

export const HEADERS = {
  acceptLanguage: 'accept-language',
  requestIp: 'bodi-request-ip',
  middlewareRewrite: 'bodi-middleware-rewrite',
  middlewareRedirect: 'bodi-middleware-redirect',
  /** Set by the Next.js client on Server Action requests. */
  nextAction: 'next-action',
  locale: 'bodi-locale',
  method: 'bodi-method',
  url: 'bodi-url',
  host: 'bodi-host',
  vercelIPCountry: 'x-vercel-ip-country',
  vercelDeploymentUrl: 'x-vercel-deployment-url',
  cloudfrontHost: 'x-cloudfront-host',
  userAgent: 'user-agent',
  shouldRedirectToSSO: 'bodi-should-redirect-to-sso',
  anonymousId: 'bodi-anonymous-id',
  /** IP-derived timezone from Vercel Proxy; not exposed by `@vercel/functions` */
  vercelIPTimezone: 'x-vercel-ip-timezone',
  outboundTimezone: 'x-timezone',
}

export const CACHE = {
  page: 'page',
  notFoundSection: '404-section',
}

export const STORAGE = {
  logsVisibleInConsole: 'logsVisibleInConsole',
  logLevelClient: 'logLevelClient',
  lastActivityTimestamp: 'lastActivityTimestamp',
  sseOrders: 'sseOrders',
  dismissedPrimaryBadges: 'dismissedPrimaryBadges',
}

export const BROADCAST_CHANNEL = 'bodi-broadcast-channel'

export const DEFAULT_LOCALE = 'en-US'

export enum BROADCAST_TOPIC {
  LOGIN = 'login',
  LOGOUT = 'logout',
}

export enum CUSTOM_EVENTS {
  stickyFooterVisible = 'stickyFooterVisible',
  stickyFooterHidden = 'stickyFooterHidden',
}

// time and dates
export const ONE_SECOND = 1000
export const ONE_MINUTE = ONE_SECOND * 60
export const ONE_HOUR = ONE_MINUTE * 60
export const ONE_DAY = ONE_HOUR * 24
export const ONE_DAY_IN_SECONDS = ONE_DAY / 1000
export const ONE_MONTH = ONE_DAY * 30
export const ONE_MONTH_IN_SECONDS = ONE_MONTH / 1000
export const ONE_YEAR = ONE_DAY * 365
export const ONE_YEAR_IN_SECONDS = ONE_YEAR / 1000

// 1px black image in base64
export const DEFAULT_IMG_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAAXNSR0IArs4c6QAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAABNJREFUCB1jZGBg+A/EDEwgAgQADigBA//q6GsAAAAASUVORK5CYII%3D'

// errors
export enum ErrorCodes {
  CustomerEmailRequired = 'customer-email-required',
  IdentityOutage = 'identity-outage',
  DuplicateCustomerTypesGranted = 'duplicate-customer-types-granted',
  DuplicateEntitlementsGranted = 'duplicate-entitlements-granted',
  InvalidItems = 'invalid-items',
  InvalidQuantity = 'invalid-quantity',
  LineItemsRequired = 'line-items-required',
  InvalidShippingCountry = 'invalid-shipping-country',
  InvalidBillingCountry = 'invalid-billing-country',
  IncompleteShippingAddress = 'incomplete-shipping-address',
  ShippingMethodRequired = 'shipping-method-required',
  CcSystemError = 'cc-system-error',
  SystemError = 'system-error',
  InvalidShippingMethod = 'invalid-shipping-method',
  TaxRequired = 'tax-required',
  InvalidDiscountCodes = 'invalid-discount-codes',
  CustomerTypeConflict = 'customer-type-conflict',
  EntitlementConflict = 'entitlement-conflict',
  CcInvalidPayment = 'cc-invalid-payment',
  CcPaymentDeclined = 'cc-payment-declined',
  CcPayment3dsDeclined = 'cc-payment-3ds-declined',
  CcInvalid3dsPayment = 'cc-invalid-3ds-payment',
  CcRequired3dsPayment = 'cc-required-3ds-payment',
  CartMissing = '[client]-cart-missing',
  LineItemsWithErrors = '[client]-line-items-with-errors',
  Auth0SSOFailure = 'auth0-sso-failure',
  Auth0CallbackError = 'auth0-callback-validation-error',
  Auth0SessionError = 'auth0-session-validation-error',
  OAuthLogin = 'oauth-login-error',
  OAuthInvalidCredentials = 'oauth-invalid-credentials-error',
  MaxEnrollmentLimit = 'max-enrollment-limit',
  Unknown = 'unknown',
}

export enum ErrorType {
  Unrecoverable = 'Unrecoverable',
  Retry = 'Retry',
  Recalculate = 'Recalculate',
  InvalidItems = 'InvalidItems',
  Eligibility = 'Eligibility',
  Payment = 'Payment',
  Unknown = 'Unknown',
  Identity = 'Identity',
}

/**
 * Error codes grouped by error type.
 * Optionally used to treat multiple errors in a single way based on the error type.
 * @example: See src/app/[country]/[lang]/checkout/modules/ErrorModal.tsx
 */
export type ErrorsGroup = Partial<Record<ErrorType, ErrorCodes[]>>

export const BuilderActionErrorsGroup: ErrorsGroup = {
  [ErrorType.Eligibility]: [ErrorCodes.CustomerTypeConflict, ErrorCodes.EntitlementConflict],
  [ErrorType.InvalidItems]: [ErrorCodes.InvalidItems],
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export const CheckoutErrorsGroup: ErrorsGroup = {
  [ErrorType.Unrecoverable]: [
    ErrorCodes.CustomerEmailRequired,
    ErrorCodes.DuplicateCustomerTypesGranted,
    ErrorCodes.DuplicateEntitlementsGranted,
  ],
  [ErrorType.Retry]: [
    ErrorCodes.LineItemsRequired,
    ErrorCodes.InvalidShippingCountry,
    ErrorCodes.InvalidBillingCountry,
    ErrorCodes.IncompleteShippingAddress,
    ErrorCodes.ShippingMethodRequired,
    ErrorCodes.CcSystemError,
    ErrorCodes.SystemError,
  ],
  [ErrorType.Recalculate]: [
    ErrorCodes.InvalidShippingMethod,
    ErrorCodes.TaxRequired,
    ErrorCodes.InvalidDiscountCodes,
  ],
  [ErrorType.InvalidItems]: [ErrorCodes.InvalidItems, ErrorCodes.LineItemsWithErrors],
  [ErrorType.Eligibility]: [ErrorCodes.CustomerTypeConflict, ErrorCodes.EntitlementConflict],
  [ErrorType.Payment]: [
    ErrorCodes.CcInvalidPayment,
    ErrorCodes.CcPaymentDeclined,
    ErrorCodes.CcPayment3dsDeclined,
    ErrorCodes.CcInvalid3dsPayment,
    ErrorCodes.CcRequired3dsPayment,
  ],
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export const OrderConfirmationErrorsGroup: ErrorsGroup = {
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export enum BuilderPageType {
  Landing = 'Landing',
  Offer = 'Offer',
  Upsell = 'Upsell',
  CreateAccount = 'Create Account',
  Category = 'Category',
  Cart = 'Cart',
  Other = 'Other',
}

export const CONSTANTS = {
  ONE_SECOND,
  ONE_MINUTE,
  ONE_HOUR,
  ONE_DAY,
  ONE_YEAR,
}

export enum CardType {
  Visa = 'Visa',
  Mastercard = 'Mastercard',
  AMEX = 'AMEX',
  Discover = 'DISCOVER',
}

export enum CardNetwork {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DISCOVER = 'DISCOVER',
  CUP = 'CUP',
}

export const CardIcons: Record<CardType, string> = {
  [CardType.Visa]: '/images/visa.svg',
  [CardType.Mastercard]: '/images/mastercard.svg',
  [CardType.AMEX]: '/images/amex.svg',
  [CardType.Discover]: '/images/discover.svg',
}

export const PaymentConstants = {
  ONE_DOLLAR_CENT_AMOUNT: 101,
  EMPTY_VALUE: '--',
  COMMERCE_INDICATOR: 'internet',
}

export const MIDDLEWARE_TIMEOUTS = {
  sessionMiddleware: 5000,
  auth0Middleware: 5000,
}

export const TBB_LANGUAGE_OPTIONS = [
  { countryCode: 'US', languages: ['en', 'es'] },
  { countryCode: 'CA', languages: ['en', 'fr'] },
  { countryCode: 'GB', languages: ['en'] },
  { countryCode: 'FR', languages: ['fr'] },
] satisfies Array<{ countryCode: CountryCodeTBB; languages: LangTBB[] }>

// Builder.io page fields used to fetch basic page data (used in sitemap and SEO)
export const BUILDER_PAGE_META_FIELDS = [
  'data.url',
  'data.title',
  'data.description',
  'data.openGraphImage',
  'data.robots',
  'data.canonicalUrl',
  'data.productKey',
  'data.seoStructuredData.includedInSitemap',
  'published',
  'query',
].join(',')

/** Crawlers to skip SSO redirects for, in addition to Next.js's `userAgent().isBot`. */
export const CRAWLER_USER_AGENTS_REGEX =
  /AhrefsBot|AhrefsSiteAudit|ContentKing|Screaming Frog SEO Spider|Conductor|SearchLight|AdIdxBot|Google-InspectionTool/i

export const AuthRoutes = [
  '/sso',
  '/login',
  '/login-callback',
  '/login-bodweb',
  '/logout',
  '/logout-callback',
  '/session',
  '/refresh',
]

export const CocoaContentType = {
  PROGRAMS: 'programs',
  BLOCKS: 'blocks',
  COMPILATIONS: 'content',
}

export const CocoaMetaEntityType = {
  category: 'category',
  level: 'level',
  trainer: 'trainer',
}

export const CocoaEntityType = {
  workout: 'workout',
  lesson: 'lesson',
  promo: 'promo',
  program: 'program',
  compilation: 'compilation',
  recipe: 'recipe',
  resource: 'resource',
  editorial: 'editorial',
  bodiBlock: 'bodiBlock',
  episode: 'episode',
  relatedContent: 'relatedContent',
}

export const CocoaCollectionType = {
  default: 'default',
  compilation: 'compilation',
  program: 'program',
  bodiBlock: 'bodiBlock',
  editorial: 'editorial',
  resource: 'resource',
  showcase: 'showcase',
  relatedContent: 'relatedContent',
  mixed: 'mixed',
  meta: 'meta',
}

export const FEATURED_PROGRAMS_SLUG = 'rail-1-featured-programs'
export const FREE_WORKOUTS_SLUG = 'bodi-previews'
export const TEN_MINUTE_PROGRAM_SLUG = '10-minute-programs'
export const DIGITAL_PURCHASES_SLUG = 'custom-my-purchases'
export const MY_TRAINER_SLUG = 'custom-my-trainer'
export const RECOMMENDED_WORKOUTS_SLUG = 'custom-recommended-for-you'
export const TRAINERS_SLUG = 'fh-trainers-short'
export const FH_CATEGORIES_SLUG = 'fh-categories-long'
export const FH_TRAINERS_SLUG = 'fh-trainers-long-programs'
export const FH_WORKOUT_DURATIONS_SLUG = 'fh-workout-durations'
export const FH_FITNESS_LEVELS_SLUG = 'fh-fitness-levels'
export const FH_EXCLUDED_PROGRAM_CATEGORY = 'nutrition'
export const FITNESS_HUB_PROGRAMS_HERO_SLUG = 'fitness-hub-programs-hero'
export const RECENTLY_WATCHED_PROGRAMS_SLUG = 'custom-recently-watched-programs'
export const RECENTLY_WATCHED_SLUG = 'recently-watched'
export const COMMERCE_RAIL_COLLECTION_SLUG = 'commerce-rail'
export const QUIZ_RESULTS_SLUG = 'custom-my-quiz-results'
/** The quiz-results resolver is LLM-ranked only on v2.0; v1.0 sorts by raw answer score. */
export const QUIZ_RESULTS_API_VERSION = 'v2.0'
export const PROGRAM_QUIZ_PATH = '/program-quiz'
export const QUIZ_QUERY_PARAM = 'quiz'
